/**
 * App-side wiring for the Work-home surfaces behind `@heidi/agents`.
 *
 * Every member crosses BY REFERENCE and is called where it was called before
 * the move: the landing's desktop-target picker is what enables the roster
 * query, the Library thumbnail's signed-URL read is scoped to a mounted card,
 * and the default-landing banner's preference read is gated on the rest of its
 * chain — resolving any of them here would move a query to a render that does
 * not happen today.
 *
 * IMPORTED FOR EFFECT BY `src/components/layout/scribe-layout.tsx`, not by the
 * Work route. The route is a Chat file, and an `@/utils` import there raises the
 * Chat ledger's zone count, which is a ruling rather than a migration. The
 * layout is the route's own static import and is evaluated before it renders.
 */
import {
  hasBootLandedOnHomeAtom,
  isHomeBootPath,
  readBootPathname,
} from '@/atoms/bootLandingAtoms';
import {
  AGENT_HOME_V1_FLAG_KEY,
  useIsDefaultLandingPageSettingEnabled,
  useIsProjectsEnabled,
} from '@/features/flags';
import { useDefaultLandingPageBannerWaitMs } from '@/features/flags/default-landing-page-banner-wait';
import { useFeatureFlagEnabled } from '@/features/flags/posthog-compat';
import { WORK_HOME_OBJECT_WORKSPACE_FLAG_KEY } from '@/features/flags/work-home-object-workspace-flag';
import { useDesktopTargetSelection } from '@/features/rpa/desktop-target/useDesktopTargetSelection';
import { DEFAULT_LANDING_PAGE_SETTINGS_HASH } from '@/features/settings/display-controls/components/default-landing-page/landing-page-options';
import { useQueryDefaultLandingPage } from '@/features/settings/display-controls/components/default-landing-page/useDefaultLandingPageSetting';
import { useSettingsModal } from '@/features/settings/hooks/useSettingsModal';
import { useOpenRoutineCreateChat } from '@/features/workflows/hooks/useOpenRoutineCreateChat';
import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import { useIsClinicalRecordingActive } from '@/hooks/useIsClinicalRecordingActive';
import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { isModalClosed } from '@/lib/modalClosedRules';
import { parseTimeStampOrNull } from '@/lib/parseTimeStamp';
import { ReactPdfDocument, ReactPdfPage } from '@/lib/pdf/react-pdf-loader';
import { trackDefaultLandingPageBannerViewed } from '@/models/analytics/features/work-home-analytics';
import { usePosthogFlagsReady } from '@/providers/posthog-flags';
import { configureWorkHomePorts, type WorkHomePorts } from '@heidi/agents';
import { getDocumentType } from '@heidi/evidence/contracts';
import { useDocumentDownloadUrl } from '@heidi/evidence/data';
import { useAtom } from 'jotai';

/** Settings → Display controls, scrolled to and highlighting the Default page row. */
const DEFAULT_LANDING_PAGE_SETTINGS_HREF = `/settings/display-controls${DEFAULT_LANDING_PAGE_SETTINGS_HASH}`;

/** The landing picker, which is also what enables the desktop-target roster query. */
const useLandingDesktopTarget = () => useDesktopTargetSelection({ surface: 'landing' });

/** The boot-landing flag as a value + setter pair: the atom itself stays app-owned. */
const useHasBootLandedOnHome = (): [boolean, (next: boolean) => void] =>
  useAtom(hasBootLandedOnHomeAtom);

/** The mark-as-viewed write, unwrapped from the mutation object. */
const useMutateMarkModalAsViewedPort = () => useMutateMarkModalAsViewed().mutate;

/**
 * The two PostHog reads, each bound to its own key so the package never sees
 * one. `undefined` is a real answer — flags have not loaded — and is passed
 * through, because that is what the mount decision holds on.
 */
const useAgentHomeNavFlag = () => useFeatureFlagEnabled(AGENT_HOME_V1_FLAG_KEY);
const useObjectWorkspaceFlag = () =>
  useFeatureFlagEnabled(WORK_HOME_OBJECT_WORKSPACE_FLAG_KEY);

export const workHomePorts: WorkHomePorts = {
  useOpenRoutineCreateChat,
  useLandingDesktopTarget,
  useDocumentDownloadUrl,
  pdf: { Document: ReactPdfDocument, Page: ReactPdfPage },
  getDocumentType,
  useIsDefaultLandingPageSettingEnabled,
  useDefaultLandingPageBannerWaitMs,
  useIsClinicalRecordingActive,
  useHasBootLandedOnHome,
  readBootPathname,
  isHomeBootPath,
  useQueryDefaultLandingPage,
  useMutateMarkModalAsViewed: useMutateMarkModalAsViewedPort,
  useSettingsModal,
  settingsHref: DEFAULT_LANDING_PAGE_SETTINGS_HREF,
  isModalClosed,
  parseTimeStampOrNull,
  trackButtonClicked,
  trackDefaultLandingPageBannerViewed,
  useAgentHomeNavFlag,
  useObjectWorkspaceFlag,
  usePosthogFlagsReady,
  useIsProjectsEnabled,
};

configureWorkHomePorts(workHomePorts);
